import { useEffect, useMemo } from 'react'
import * as Y from 'yjs'
import { Y_ELEMENTS, Y_FRAMES, Y_META } from '@praesto/shared'

export interface UndoControls {
  undo: () => void
  redo: () => void
}

/**
 * Rückgängig und Wiederherstellen.
 *
 * Yjs' `UndoManager` verfolgt standardmäßig nur Änderungen mit dem Ursprung
 * `null` — also genau die eigenen. Was über den WebSocket hereinkommt, trägt
 * den Provider als Ursprung und bleibt unangetastet. Deshalb nimmt „Rückgängig"
 * hier nur zurück, was man selbst getan hat, und reißt niemandem die Arbeit weg.
 */
export function useUndo(doc: Y.Doc, enabled: boolean): UndoControls {
  const manager = useMemo(
    () =>
      new Y.UndoManager(
        [doc.getMap(Y_ELEMENTS), doc.getMap(Y_FRAMES), doc.getMap(Y_META)],
        // Schritte, die dichter aufeinander folgen, werden zusammengefasst —
        // sonst nimmt jeder Pixel einer Ziehbewegung einen eigenen Schritt ein.
        { captureTimeout: 400 },
      ),
    [doc],
  )

  useEffect(() => () => manager.destroy(), [manager])

  useEffect(() => {
    if (!enabled) return
    const onKeyDown = (event: KeyboardEvent): void => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'z') return
      const target = event.target
      // In einem Textfeld gehört ⌘Z dem Textfeld.
      if (
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return
      }
      event.preventDefault()
      if (event.shiftKey) manager.redo()
      else manager.undo()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [enabled, manager])

  return {
    undo: () => manager.undo(),
    redo: () => manager.redo(),
  }
}
